import React, { useState, useRef, useEffect, useMemo, useContext } from 'react';
import axios from 'axios';
import { themeColors } from '../theme';
import PlotExportBar from './PlotExportBar';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';
import { jvXDomain, jvYDomain as jvYDomainAuto, niceStep, buildTicks } from '../utils/chartDomain';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;
const fmt    = (v, d = 3) => v != null && isFinite(v) ? Number(v).toFixed(d) : '—';
const fmtExp = (v) => v != null && isFinite(v) && v > 0 ? Number(v).toExponential(3) : '—';
const KTQ = 0.02585;

const ML = 14, MR = 16, MT = 4, MB = 50, YW = 88, CH = 240;
// AX / TK are re-declared per-render inside CurvaPanel with theme-aware
// fill so the module-level consts would be shadowed anyway.
const CM = { top: MT, right: MR, bottom: MB, left: ML };
const tickFmt = (decimals) => (v) => {
  if (!isFinite(v)) return '';
  if (Math.abs(v) >= 0.001 && Math.abs(v) < 10000) return Number(v.toFixed(decimals)).toString();
  return v.toPrecision(3);
};

// Color scheme — consistent across plots and cursor lines
const C = {
  jv:   '#00d4ff',   // plot 1: JV curve — blue
  rsh:  '#39d353',   // plot 2: dJ/dV and Rsh cursor — green
  rs:   '#ff4757',   // plot 3: dV/dJ and Rs cursors — red
  logj: '#f0a500',   // plot 4: log(J) and J0 cursors — yellow
};

function linReg(xs, ys) {
  const n = xs.length; if (n < 2) return null;
  const mx = xs.reduce((a,b)=>a+b)/n, my = ys.reduce((a,b)=>a+b)/n;
  let num=0, den=0;
  for (let i=0;i<n;i++){num+=(xs[i]-mx)*(ys[i]-my);den+=(xs[i]-mx)**2;}
  if (Math.abs(den)<1e-12) return null;
  const s=num/den, ic=my-s*mx;
  let sr=0,st=0;
  for (let i=0;i<n;i++){sr+=(ys[i]-(s*xs[i]+ic))**2;st+=(ys[i]-my)**2;}
  return {slope:s,intercept:ic,r2:st>0?1-sr/st:1};
}
function computeRsh(arr, vC) {
  let best=null,bd=Infinity;
  for (const p of arr){const d=Math.abs(p.V-vC);if(d<bd){bd=d;best=p;}}
  if(!best||Math.abs(best.dJdV)<1e-10) return null;
  return 1/Math.abs(best.dJdV)*1000;
}
function computeRsA(arr, invJMin, invJMax) {
  const pts=arr.filter(p=>p.invJ>=invJMin&&p.invJ<=invJMax&&p.invJ>0&&p.dVdJ>0&&p.dVdJ<50);
  if(pts.length<2) return null;
  const fit=linReg(pts.map(p=>p.invJ),pts.map(p=>p.dVdJ)); if(!fit) return null;
  const A=fit.slope*1e-3/KTQ;
  return {Rs:isFinite(fit.intercept)&&fit.intercept>=0?fit.intercept:null,A_dVdJ:A>0.1&&A<10?A:null,r2:fit.r2};
}
function computeJ0A(arr, vMin, vMax) {
  const pts=arr.filter(p=>p.V>=vMin&&p.V<=vMax); if(pts.length<2) return null;
  const fit=linReg(pts.map(p=>p.V),pts.map(p=>p.logJ)); if(!fit||fit.slope<=0) return null;
  const A=1/(fit.slope*KTQ*2.302585), J0=Math.pow(10,fit.intercept);
  return {A_logJV:A>0.1&&A<10?A:null,J0:J0>1e-30&&J0<1e3?J0:null,r2:fit.r2};
}
function reconstructMetrics(Jsc_meas, J0, A, Rs, Rsh) {
  // Reconstruct Voc, FF, PCE, Vmp, Jmp from single-diode model parameters
  // Uses measured |Jsc| (positive) and interactive J0, A, Rs, Rsh
  if (Jsc_meas==null||J0==null||A==null) return null;
  const nVt=A*KTQ, Jsc=Math.abs(Jsc_meas);
  if (J0<=0||nVt<=0||Jsc<=0) return null;
  const rs=Rs||0, rsh=Rsh||1e6;
  // Voc: solve J=0 → J0*[exp(Voc/(A*Vt))-1]+Voc/Rsh=Jsc  (Newton)
  let Voc=nVt*Math.log(Jsc/J0+1);
  for (let i=0;i<50;i++){
    const eV=Math.exp(Voc/nVt);
    const f=J0*(eV-1)+Voc/rsh-Jsc;
    const fp=J0*eV/nVt+1/rsh;
    if (Math.abs(fp)<1e-30) break;
    const dv=f/fp; Voc-=dv;
    if (Math.abs(dv)<1e-8) break;
  }
  if (!isFinite(Voc)||Voc<=0) return null;
  // JV sweep to find MPP
  let Pmax=0,Vmp=0,Jmp=0;
  for (let k=1;k<200;k++){
    const V=Voc*k/200;
    // Solve for J at this V: J=Jsc-J0*(exp((V+J*Rs)/(nVt))-1)-(V+J*Rs)/Rsh
    let J=Jsc-J0*(Math.exp(V/nVt)-1)-V/rsh;
    for (let it=0;it<30;it++){
      const Vd=V+J*rs;
      const eVd=Math.exp(Math.min(Vd/nVt,500));
      const Jn=Jsc-J0*(eVd-1)-Vd/rsh;
      const dJdJ=-J0*eVd*rs/nVt-rs/rsh;
      const dJ=(Jn-J)/(1-dJdJ);
      J+=dJ; if(Math.abs(dJ)<1e-10) break;
    }
    const P=V*J;
    if (P>Pmax){Pmax=P;Vmp=V;Jmp=J;}
  }
  const FF=(Voc>0&&Jsc>0)?Pmax/(Voc*Jsc):0;
  const PCE=Pmax/100*100; // Pin=100 mW/cm²
  return {Voc,Jsc,FF:FF*100,PCE,Vmp,Jmp,Pmp:Pmax};
}

const OrangeDot = (props) => {
  const { cx, cy, payload } = props;
  if (!payload?.highlighted || !isFinite(cx) || !isFinite(cy)) return null;
  return <circle cx={cx} cy={cy} r={6} fill={C.logj} opacity={0.95} stroke="#fff" strokeWidth={1}/>;
};

// InteractiveChart — draggable vertical cursor lines
// Flag position: top 25% of plot height (not middle) so flags don't overlap data
function InteractiveChart({ children, lines, xMin, xMax }) {
  const ref = useRef();
  const [cw, setCw] = useState(0);
  const draggingIdx = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const upd = () => setCw(ref.current.getBoundingClientRect().width);
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const plotLeft  = ML + YW;
  const plotWidth = Math.max(1, cw - ML - YW - MR);

  const clientToV = (clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return null;
    const relX = clientX - rect.left - plotLeft;
    const f = Math.max(0, Math.min(1, relX / plotWidth));
    return Math.round((xMin + f * (xMax - xMin)) * 100) / 100;
  };

  const onMouseDown = (idx) => (e) => {
    e.preventDefault();
    draggingIdx.current = idx;
    const move = (ev) => {
      if (draggingIdx.current === null) return;
      const v = clientToV(ev.clientX);
      if (v !== null) lines[draggingIdx.current].onDrag(v);
    };
    const up = () => {
      draggingIdx.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return (
    <div ref={ref} style={{ position: 'relative', overflow: 'hidden' }}>
      {children}
      {cw > 0 && lines && lines.map((ln, i) => {
        const frac = Math.max(0, Math.min(1, (ln.x - xMin) / (xMax - xMin)));
        const px = plotLeft + frac * plotWidth;
        const flagTop = MT + 10 + i * 32;
        return (
          <React.Fragment key={i}>
            {/* Cursor line */}
            <div onMouseDown={onMouseDown(i)} style={{
              position: 'absolute',
              left: px - 1,
              top: MT,
              bottom: MB,
              width: 3,
              background: ln.color,
              cursor: 'ew-resize',
              zIndex: 20,
              userSelect: 'none',
            }} />
            {/* Flag — anchored at px, sits left of the line, text rotated vertical */}
            <div style={{
              position: 'absolute',
              left: px - 14,          // 14px left of line center gives clear gap
              top: flagTop,
              transform: 'translateX(-100%) rotate(-90deg)',
              transformOrigin: '100% 50%',
              fontFamily: 'IBM Plex Mono,monospace', fontSize: 10,
              color: ln.color,
              background: 'rgba(8,11,15,0.92)',
              padding: '2px 6px', borderRadius: 2,
              whiteSpace: 'nowrap', pointerEvents: 'none',
              border: `1px solid ${ln.color}`,
              zIndex: 19,
            }}>{ln.label} {Number(ln.x).toFixed(3)}</div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

const DEF = { rsh_V:0.0, rs_invJ_min:0.02, rs_invJ_max:0.12, logj_V_min:0.20, logj_V_max:0.80 };

export default function CurvaPanel({ simulationResults, theme = 'dark' }) {
  const c = themeColors(theme);
  // Theme-aware overrides for the module-level AX / TK / CM / YW / CH
  // constants used by every chart in this file.  Shadowing the imports
  // lets all chart JSX keep the same names.
  const AX = { fill: c.plotText, fontSize: 13, fontFamily: 'IBM Plex Mono' };
  const TK = { fill: c.plotText, fontSize: 13, fontFamily: 'IBM Plex Mono' };
  const [cursors, setCursors]       = useState(DEF);
  const [manualJ0, setManualJ0]     = useState('');
  const [manualA,  setManualA]      = useState('');
  const [uploadedJV, setUploadedJV] = useState(null);
  const [uploadMeta, setUploadMeta] = useState(null);
  const [dataSource, setDataSource] = useState('simulated');
  // 'mA/cm2' | 'A/cm2' | 'uA/cm2' — unit the uploaded J values were in
  // BEFORE we rescaled them into mA/cm² internally.
  const [uploadedUnit, setUploadedUnit] = useState('mA/cm2');
  // The raw J array the backend returned, kept so the unit selector can
  // rescale without re-uploading.
  const [uploadedRawJ, setUploadedRawJ] = useState(null);
  const [savedResult, setSavedResult] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState(null);

  // ── Stage 2: publish to WorkspaceContext (curva block). ───────────
  // `uploaded_jv_inline` and `fit_results` are placeholders here; the
  // < 200-point inline rule (§3.3a) and the fit-extraction wire-up land
  // in Stage 3 alongside the new endpoint shape.
  const publishCtx = useContext(WorkspaceContextSetter);
  useEffect(() => {
    publishCtx('curva', {
      data_source: dataSource,
      has_uploaded_jv: !!uploadedJV,
      uploaded_meta: uploadMeta,
      uploaded_jv_inline: null,
      cursors,
      manual_j0: manualJ0 === '' ? null : parseFloat(manualJ0),
      manual_A:  manualA  === '' ? null : parseFloat(manualA),
      fit_results: null,
      saving,
      save_error: saveError,
    });
  }, [publishCtx, dataSource, uploadedJV, uploadMeta, cursors,
      manualJ0, manualA, saving, saveError]);
  const fileRef = useRef();

  const setC = (k, v) => setCursors(c => ({...c, [k]: v}));

  // null = auto (data-driven); manual entry in AxisControls overrides.
  const [jvAxis, setJvAxis] = useState({vMin:null, vMax:null, vStep:null, jMin:null, jMax:null, jStep:5});
  const setJvA = (k,v) => setJvAxis(a=>({...a,[k]:v}));

  const jv = useMemo(() => {
    if (dataSource==='uploaded' && uploadedJV) return uploadedJV;
    if (dataSource==='simulated' && simulationResults?.jv_curve)
      // Backend sends J<0 for photocurrent (standard physics sign convention).
      return {V:simulationResults.jv_curve.map(p=>p.V), J:simulationResults.jv_curve.map(p=>p.J)};
    return null;
  }, [dataSource, uploadedJV, simulationResults?.jv_curve]);
  const auto = simulationResults?.metrics || null;
  const vMin = jv ? Math.min(...jv.V) : -0.1;
  const vMax = jv ? Math.max(...jv.V) :  1.2;

  const jvData = useMemo(()=> jv ? jv.V.map((v,i)=>({V:v,J:jv.J[i]})) : [], [jv]);

  const djdvData = useMemo(()=>{
    if (!jv) return [];
    const out=[];
    for (let i=1;i<jv.V.length-1;i++){
      const dj=(jv.J[i+1]-jv.J[i-1])/(jv.V[i+1]-jv.V[i-1]);
      if (isFinite(dj)&&Math.abs(dj)<500) out.push({V:jv.V[i],dJdV:dj});
    }
    return out;
  },[jv]);

  const dvdjData = useMemo(()=>{
    if (!jv) return [];
    // Sites method: dV/dJ vs 1/(J+Jsc) where J+Jsc = dark diode component
    const i0=jv.V.reduce((bi,v,i)=>Math.abs(v)<Math.abs(jv.V[bi])?i:bi,0);
    const Jsc=Math.abs(jv.J[i0]);
    const out=[];
    for (let i=1;i<jv.V.length;i++){
      const dv=jv.V[i]-jv.V[i-1], dj=jv.J[i]-jv.J[i-1];
      const Jm=0.5*(jv.J[i]+jv.J[i-1]);
      const Jdark=Jm+Jsc; // dark diode current component
      if (Math.abs(dj)>1e-6 && Jdark > 0.5){
        const dvdj = (dv/dj)*1000; // V/(mA/cm²) → Ω·cm²
        const invJ = 1/Jdark;
        if (isFinite(dvdj) && isFinite(invJ) && dvdj > -5 && dvdj < 50 && invJ > 0 && invJ < 2)
          out.push({invJ, dVdJ:dvdj, V:jv.V[i]});
      }
    }
    return out.sort((a,b)=>a.invJ-b.invJ);
  },[jv]);

  const logjData = useMemo(()=>{
    if (!jv) return [];
    const i0=jv.V.reduce((bi,v,i)=>Math.abs(v)<Math.abs(jv.V[bi])?i:bi,0);
    const Jsc=Math.abs(jv.J[i0]);
    return jv.V.map((v,i)=>{
      const Jw=jv.J[i]+Jsc;
      if (v>0.05&&Jw>1e-8){const lj=Math.log10(Jw);return isFinite(lj)?{V:v,logJ:lj}:null;}
      return null;
    }).filter(Boolean);
  },[jv]);

  // Auto-adjust cursor positions when new data arrives
  useEffect(()=>{
    if (!jv) return;
    // Estimate Voc from zero-crossing
    let Voc = 0.9;
    for (let i=0;i<jv.V.length-1;i++){
      if ((jv.J[i]<0 && jv.J[i+1]>=0)||(jv.J[i]>=0 && jv.J[i+1]<0)){
        const f=-jv.J[i]/(jv.J[i+1]-jv.J[i]);
        Voc=jv.V[i]+f*(jv.V[i+1]-jv.V[i]); break;
      }
    }
    // Set logJ window to [0.15*Voc, 0.80*Voc] — the exponential region
    const lMin=Math.round(Voc*0.15*100)/100;
    const lMax=Math.round(Voc*0.80*100)/100;
    // Rs Siles window: use voltage between 0.7*Voc and Voc, translated
    // into 1/J bounds from the actual data.  Fallback to the classic
    // [0.5, 2.5] × 1/Jsc band if the voltage-anchored window is empty.
    const i0=jv.V.reduce((bi,v,i)=>Math.abs(v)<Math.abs(jv.V[bi])?i:bi,0);
    const Jsc=Math.abs(jv.J[i0]);
    const invJsc = Jsc > 0 ? 1/Jsc : 0.05;
    const invJsInRsBand = [];
    for (let i=0;i<jv.V.length;i++){
      if (jv.V[i] >= 0.7*Voc && jv.V[i] <= Voc) {
        // Convert raw J (light, Jsc-shifted) to dark-diode-positive.
        const Jdark = jv.J[i] + Jsc;
        if (Jdark > 1e-6) invJsInRsBand.push(1/Jdark);
      }
    }
    let rsMin, rsMax;
    if (invJsInRsBand.length >= 2) {
      rsMin = Math.min(...invJsInRsBand);
      rsMax = Math.max(...invJsInRsBand);
    } else {
      rsMin = invJsc * 0.5;
      rsMax = invJsc * 2.5;
    }
    setCursors({
      rsh_V: 0.0,
      rs_invJ_min: Math.round(rsMin * 1000) / 1000,
      rs_invJ_max: Math.round(rsMax * 1000) / 1000,
      logj_V_min: lMin,
      logj_V_max: lMax,
    });
  },[jv]);

  const liveRsh = useMemo(()=>computeRsh(djdvData,cursors.rsh_V),[djdvData,cursors.rsh_V]);
  const liveRsA = useMemo(()=>computeRsA(dvdjData,cursors.rs_invJ_min,cursors.rs_invJ_max),[dvdjData,cursors.rs_invJ_min,cursors.rs_invJ_max]);
  const liveJ0A = useMemo(()=>computeJ0A(logjData,cursors.logj_V_min,cursors.logj_V_max),[logjData,cursors.logj_V_min,cursors.logj_V_max]);

  const effJ0 = manualJ0!=='' ? parseFloat(manualJ0) : liveJ0A?.J0;
  const effA  = manualA !=='' ? parseFloat(manualA)  : liveJ0A?.A_logJV;

  // Reconstruct JV metrics from interactive fit parameters
  const liveMetrics = useMemo(()=>{
    const Jsc_meas = auto?.Jsc;
    return reconstructMetrics(Jsc_meas, effJ0, effA, liveRsA?.Rs, liveRsh);
  },[auto?.Jsc, effJ0, effA, liveRsA?.Rs, liveRsh]);

  const jvDataHL = useMemo(()=>jvData.map(p=>({
    ...p, highlighted: p.V>=cursors.logj_V_min && p.V<=cursors.logj_V_max
  })),[jvData,cursors.logj_V_min,cursors.logj_V_max]);

  // ── Data-driven JV axis domains (chartDomain.js helpers) ────────────
  // X spans the data extents and always extends past the zero crossing
  // so the Voc intercept is visible — no hardcoded [-0.1, 1.2] default.
  const jvAutoX = useMemo(() => jvXDomain([jvData]), [jvData]);
  const jvXLo = jvAxis.vMin != null ? jvAxis.vMin : (jvAutoX ? jvAutoX[0] : -0.1);
  const jvXHi = jvAxis.vMax != null ? jvAxis.vMax : (jvAutoX ? jvAutoX[1] : 1.2);
  const jvVStep = jvAxis.vStep || niceStep(jvXHi - jvXLo, 7);
  const jvVTicks = buildTicks(jvXLo, jvXHi, jvVStep);

  const jvYDomain = useMemo(()=>{
    if (jvAxis.jMin!=null && jvAxis.jMax!=null) return [jvAxis.jMin, jvAxis.jMax];
    const auto = jvYDomainAuto([jvData], jvXLo, jvXHi);
    return auto || ['auto','auto'];
  },[jvData, jvAxis.jMin, jvAxis.jMax, jvXLo, jvXHi]);

  // Auto-detect likely current units from the magnitude of |J|.
  //   |J|_max < 0.1   → A/cm²    (scale ×1000 into mA/cm²)
  //   |J|_max > 1000  → µA/cm²   (scale ÷1000 into mA/cm²)
  //   else            → mA/cm²   (as-is)
  function detectJUnit(Jraw) {
    const vals = (Jraw || []).filter(v => isFinite(v)).map(Math.abs);
    if (!vals.length) return 'mA/cm2';
    const mx = Math.max(...vals);
    if (mx < 0.1)   return 'A/cm2';
    if (mx > 1000)  return 'uA/cm2';
    return 'mA/cm2';
  }
  function rescaleJ(Jraw, unit) {
    if (!Jraw) return null;
    const factor = unit === 'A/cm2'  ? 1000
                 : unit === 'uA/cm2' ? 0.001
                 :                     1.0;
    return Jraw.map(v => v * factor);
  }

  const handleUpload = async (e) => {
    const file=e.target.files[0]; if(!file) return;
    const fd=new FormData(); fd.append('file',file);
    try{
      const {data}=await axios.post(`${API}/curva/upload`,fd);
      const detected = detectJUnit(data.J);
      const Jscaled  = rescaleJ(data.J, detected);
      setUploadedRawJ(data.J);
      setUploadedUnit(detected);
      setUploadedJV({V:data.V,J:Jscaled}); setUploadMeta(data.meta); setDataSource('uploaded');
    }catch(err){setSaveError('Upload: '+(err.response?.data?.detail||err.message));}
  };

  // Manual override from the unit selector — rescale the cached raw J.
  function changeUploadedUnit(unit) {
    setUploadedUnit(unit);
    if (uploadedRawJ) {
      setUploadedJV({V:uploadedJV?.V || [], J:rescaleJ(uploadedRawJ, unit)});
    }
  }

  const handleSave = async () => {
    if (!jv){setSaveError('No JV data.');return;}
    setSaving(true);setSaveError(null);setSavedResult(null);
    try{
      const {data}=await axios.post(`${API}/curva/manual-fit`,{
        V:jv.V,J:jv.J,illuminated:true,
        rsh_V_min:cursors.rsh_V-0.05,rsh_V_max:cursors.rsh_V+0.05,
        rs_invJ_min:cursors.rs_invJ_min,rs_invJ_max:cursors.rs_invJ_max,
        logj_V_min:cursors.logj_V_min,logj_V_max:cursors.logj_V_max,
        J0_guess:manualJ0?parseFloat(manualJ0):null,
        A_guess:manualA?parseFloat(manualA):null,Rs_guess:null,
      });
      setSavedResult(data);
    }catch(err){setSaveError(err.response?.data?.detail||err.message);}
    finally{setSaving(false);}
  };

  const inp={background: c.input,border: `1px solid ${c.border}`,borderRadius:4,color: c.inputText,fontFamily:'IBM Plex Mono,monospace',fontSize:14,padding:'5px 8px',outline:'none',width:'100%'};
  const sl={fontSize:14,letterSpacing:'2px',textTransform:'uppercase',color: c.textMuted,marginBottom:10};

  return (
    <div style={{display:'flex',height:'100%',overflow:'hidden',fontFamily:'IBM Plex Mono,monospace'}}>

      {/* LEFT PANEL */}
      <div style={{width:270,background: c.bg,borderRight: `1px solid ${c.border}`,overflowY:'auto',display:'flex',flexDirection:'column',flexShrink:0}}>

        <div style={{padding:'14px 16px',borderBottom: `1px solid ${c.border}`}}>
          <div style={sl}>Data Source</div>
          <div style={{display:'flex',gap:6,marginBottom:10}}>
            {['simulated','uploaded'].map(s=>{
              const active = dataSource === s;
              const disabled = s === 'uploaded' && !uploadedJV;
              return (
                <button key={s} onClick={() => setDataSource(s)} disabled={disabled}
                  style={{
                    flex: 1, padding: '6px 12px', borderRadius: 4,
                    fontSize: 13, fontWeight: active ? 600 : 400,
                    fontFamily: 'IBM Plex Mono, monospace',
                    background: active ? c.btnActive : c.btnSecondary,
                    color: active ? c.btnPrimaryText : c.btnSecondaryText,
                    border: `1px solid ${active ? c.accent : c.border}`,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                  }}>
                  {s === 'simulated' ? 'Simulated' : 'Uploaded'}
                </button>
              );
            })}
          </div>
          <button onClick={()=>fileRef.current.click()}
            style={{...inp,
              background: c.bgSecondary, color: c.text,
              border: `1px dashed ${c.border}`,
              cursor: 'pointer', textAlign: 'center', display: 'block'}}>
            ↑ Upload JV File (CSV / txt)
          </button>
          <input ref={fileRef} type="file" accept=".csv,.txt,.dat" style={{display:'none'}} onChange={handleUpload}/>
          {uploadMeta&&<div style={{fontSize:12,color: c.textMuted,marginTop:8,lineHeight:1.6}}>{uploadMeta.filename}<br/>{uploadMeta.n_points} pts</div>}
          {uploadedJV && (
            <div style={{marginTop:10}}>
              <div style={{fontSize:11,color: c.textMuted,letterSpacing:'1px',textTransform:'uppercase',marginBottom:4}}>Current units</div>
              <select value={uploadedUnit}
                onChange={e => changeUploadedUnit(e.target.value)}
                style={{...inp,cursor:'pointer'}}>
                <option value="mA/cm2">mA/cm²</option>
                <option value="A/cm2">A/cm²</option>
                <option value="uA/cm2">µA/cm²</option>
              </select>
              {uploadedRawJ && uploadedUnit !== 'mA/cm2' && (
                <div style={{fontSize:11,color:'#f0a500',marginTop:6,lineHeight:1.4}}>
                  Detected: {uploadedUnit === 'A/cm2' ? 'A/cm²' : 'µA/cm²'} — converted to mA/cm²
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{padding:'14px 16px',borderBottom: `1px solid ${c.border}`}}>
          <div style={sl}>Live Fit Results</div>
          {[
            ['Rsh',       liveRsh!=null?`${fmt(liveRsh,1)} Ω·cm²`:'—',  C.rsh],
            ['Rs',        liveRsA?.Rs!=null?`${fmt(liveRsA.Rs,4)} Ω·cm²`:'—', C.rs],
            ['A (dV/dJ)', liveRsA?.A_dVdJ!=null?fmt(liveRsA.A_dVdJ,3):'—', C.rs],
            ['J₀',        liveJ0A?.J0!=null?fmtExp(liveJ0A.J0)+' mA/cm²':'—', C.logj],
            ['A (logJ)',  liveJ0A?.A_logJV!=null?fmt(liveJ0A.A_logJV,3):'—', C.logj],
          ].map(([n,v,c])=>(
            <div key={n} style={{display:'flex',justifyContent:'space-between',padding:'5px 0',borderBottom:'1px solid #0d1117'}}>
              <span style={{fontSize:15,color: c.textMuted}}>{n}</span>
              <span style={{fontSize:16,color:c,fontWeight:500}}>{v}</span>
            </div>
          ))}
          {liveRsA?.r2!=null&&<div style={{fontSize:12,color:'#4a6278',marginTop:5}}>Rs R² = {liveRsA.r2.toFixed(4)}</div>}
          {liveJ0A?.r2!=null&&<div style={{fontSize:12,color:'#4a6278'}}>logJ R² = {liveJ0A.r2.toFixed(4)}</div>}
        </div>

        <div style={{padding:'14px 16px',borderBottom: `1px solid ${c.border}`}}>
          <div style={sl}>Manual Override — J₀ &amp; A</div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,color: c.textMuted,marginBottom:4,display:'flex',justifyContent:'space-between'}}><span>J₀</span><span style={{fontSize:12}}>mA/cm²</span></div>
            <input type="text" style={inp} placeholder={liveJ0A?.J0?fmtExp(liveJ0A.J0):'auto'} value={manualJ0} onChange={e=>setManualJ0(e.target.value)}/>
            <input type="range" min="-18" max="0" step="0.1"
              value={manualJ0!==''?Math.log10(Math.max(1e-18,parseFloat(manualJ0)||1e-10)):(liveJ0A?.J0?Math.log10(Math.max(1e-18,liveJ0A.J0)):-10)}
              onChange={e=>setManualJ0(Math.pow(10,parseFloat(e.target.value)).toExponential(3))}
              style={{width:'100%',marginTop:7,accentColor:C.logj}}/>
            <div style={{fontSize:11,color:'#4a6278',display:'flex',justifyContent:'space-between',marginTop:2}}>
              <span>1e-18</span><span style={{color:C.logj}}>Active: {fmtExp(effJ0)}</span><span>1e0</span>
            </div>
          </div>
          <div>
            <div style={{fontSize:13,color: c.textMuted,marginBottom:4}}>Ideality Factor A</div>
            <input type="text" style={inp} placeholder={liveJ0A?.A_logJV?fmt(liveJ0A.A_logJV,3):'auto'} value={manualA} onChange={e=>setManualA(e.target.value)}/>
            <input type="range" min="0.5" max="5" step="0.01"
              value={manualA!==''?(parseFloat(manualA)||1):(liveJ0A?.A_logJV||2)}
              onChange={e=>setManualA(parseFloat(e.target.value).toFixed(3))}
              style={{width:'100%',marginTop:7,accentColor:C.logj}}/>
            <div style={{fontSize:11,color:'#4a6278',display:'flex',justifyContent:'space-between',marginTop:2}}>
              <span>0.5</span><span style={{color:C.logj}}>Active: {effA!=null?fmt(effA,3):'—'}</span><span>5.0</span>
            </div>
          </div>
        </div>

        {/* Cursor positions — colored to match their plots */}
        <div style={{padding:'14px 16px',borderBottom: `1px solid ${c.border}`}}>
          <div style={sl}>Cursor Positions</div>
          {[
            ['Rsh V',        'rsh_V',         C.rsh],
            ['Rs 1/J min',   'rs_invJ_min',   C.rs],
            ['Rs 1/J max',   'rs_invJ_max',   C.rs],
            ['logJ V min',   'logj_V_min',    C.logj],
            ['logJ V max',   'logj_V_max',    C.logj],
          ].map(([lab,key,col])=>(
            <div key={key} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:7}}>
              <span style={{fontSize:14,color:col,flex:1}}>{lab}</span>
              <input type="text" value={cursors[key]}
                onChange={e=>{const v=parseFloat(e.target.value);if(!isNaN(v))setC(key,v);}}
                style={{...inp,width:70,textAlign:'right',color:col,padding:'3px 6px',fontSize:13}}/>
            </div>
          ))}
        </div>

        <div style={{padding:'14px 16px',marginTop:'auto',borderTop:'1px solid #1e2d3d',background: c.bg,position:'sticky',bottom:0}}>
          {saveError&&<div style={{fontSize:12,color:'#ff4757',marginBottom:8}}>{saveError}</div>}
          <button onClick={handleSave} disabled={saving}
            style={{width:'100%',padding:'11px',fontFamily:'IBM Plex Mono,monospace',fontSize:14,fontWeight:500,
              letterSpacing:'1px',textTransform:'uppercase',background:'transparent',
              border:`1px solid ${saving?C.logj:'#00d4ff'}`,color:saving?C.logj:'#00d4ff',
              borderRadius:4,cursor:saving?'not-allowed':'pointer'}}>
            {saving?'⟳  Saving...':'✓  Save to Table'}
          </button>
          {savedResult&&<div style={{fontSize:12,color:C.rsh,marginTop:8,textAlign:'center'}}>Saved ✓</div>}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{flex:1,overflowY:'auto',background:c.bg,display:'flex',flexDirection:'column'}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',borderBottom: `1px solid ${c.border}`}}>

          {/* Plot 1: JV */}
          <div style={{padding:'16px 20px',borderRight: `1px solid ${c.border}`,borderBottom: `1px solid ${c.border}`}}>
            <PT col={c.accentDark} c={c}>1 — J–V Characteristic</PT>
            <Sub col={c.isDark ? '#00d4ff' : '#0052cc'} c={c}>● Highlighted: logJ window [{Number(cursors.logj_V_min).toFixed(2)}, {Number(cursors.logj_V_max).toFixed(2)}] V</Sub>
            {jvData.length>0?(
              <>
                <div id="curva-chart-0" style={{position:'relative',height:CH,background:c.plotBg,borderRadius:6,border:`1px solid ${c.border}`}}>
                  <ResponsiveContainer width="100%" height={CH}>
                    <LineChart data={jvDataHL} margin={CM}>
                      <CartesianGrid strokeDasharray="3 3" stroke={c.plotGrid}/>
                      <XAxis dataKey="V" type="number"
                        domain={[jvXLo, jvXHi]}
                        ticks={jvVTicks}
                        tickFormatter={v=>v.toFixed(1)} tick={TK}
                        label={{value:'Voltage (V)',position:'insideBottom',offset:-18,...AX}}/>
                      <YAxis width={YW} domain={jvYDomain} allowDataOverflow={true}
                        ticks={jvAxis.jMin!=null&&jvAxis.jMax!=null&&jvAxis.jStep
                          ? Array.from({length:Math.round((jvAxis.jMax-jvAxis.jMin)/jvAxis.jStep)+1},
                              (_,i)=>Math.round((jvAxis.jMin+i*jvAxis.jStep)*100)/100)
                          : undefined}
                        tickFormatter={tickFmt(1)} tick={TK}
                        label={{value:'J (mA/cm²)',angle:-90,position:'center',dx:-10,...AX}}/>
                      <Tooltip formatter={v=>typeof v==='number'?v.toFixed(3):v}/>
                      <ReferenceLine y={0} stroke={c.plotGrid} strokeDasharray="4 2"/>
                      <ReferenceLine x={0} stroke={c.plotGrid} strokeDasharray="4 2"/>
                      <Line type="monotone" dataKey="J" stroke={C.jv} strokeWidth={2}
                        dot={<OrangeDot/>} activeDot={false} isAnimationActive={false} name="J"/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <AxisControls c={c} fields={[
                  {label:'V min', val:jvAxis.vMin??'auto', onChange:v=>setJvA('vMin',v), step:jvAxis.vStep??0.1},
                  {label:'V max', val:jvAxis.vMax??'auto', onChange:v=>setJvA('vMax',v), step:jvAxis.vStep??0.1},
                  {label:'V step',val:jvAxis.vStep??'auto',onChange:v=>setJvA('vStep',v), step:0.05},
                  {label:'J min', val:jvAxis.jMin??'auto', onChange:v=>setJvA('jMin',v), step:jvAxis.jStep},
                  {label:'J max', val:jvAxis.jMax??'auto', onChange:v=>setJvA('jMax',v), step:jvAxis.jStep},
                  {label:'J step',val:jvAxis.jStep, onChange:v=>setJvA('jStep',v), step:1},
                ]}/>
              </>
            ):<ND c={c}/>}
            <PlotExportBar chartId="curva-chart-0"
              csvData={(jv ? jv.V.map((v,i)=>({ V:v, J:jv.J[i],
                highlighted: v>=cursors.logj_V_min && v<=cursors.logj_V_max ? 1 : 0 })) : [])}
              csvFilename="curva_jv" plotFilename="curva_jv"
              c={c} disabled={!jv} />
          </div>

          {/* Plot 2: dJ/dV — GREEN */}
          <div style={{padding:'16px 20px',borderBottom: `1px solid ${c.border}`}}>
            <PT col={c.isDark ? '#3fb950' : '#1a7a2e'} c={c}>2 — dJ/dV vs V → R<sub>sh</sub>{liveRsh!=null&&<Res col={c.isDark ? '#3fb950' : '#1a7a2e'}>= {fmt(liveRsh,1)} Ω·cm²</Res>}</PT>
            <Sub col={c.isDark ? '#3fb950' : '#1a7a2e'} c={c}>● Drag green line → Rsh extraction point</Sub>
            {djdvData.length>0?(
              <div id="curva-chart-1">
              <InteractiveChart xMin={vMin} xMax={vMax} lines={[
                {color:C.rsh, x:cursors.rsh_V, label:'Rsh', onDrag:v=>setC('rsh_V',v)},
              ]}>
                <ResponsiveContainer width="100%" height={CH}>
                  <LineChart data={djdvData} margin={CM}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="V" tickFormatter={v=>v.toFixed(2)} tick={TK}
                      label={{value:'Voltage (V)',position:'insideBottom',offset:-18,...AX}}/>
                    <YAxis width={YW} tickFormatter={tickFmt(1)} tick={TK}
                      label={{value:'dJ/dV (mS/cm²)',angle:-90,position:'center',dx:-10,...AX}}/>
                    <Tooltip formatter={v=>typeof v==='number'?v.toFixed(3):v}/>
                    <Line type="monotone" dataKey="dJdV" stroke={C.rsh} strokeWidth={2} dot={false} name="dJ/dV"/>
                  </LineChart>
                </ResponsiveContainer>
              </InteractiveChart>
              </div>
            ):<ND c={c}/>}
            <PlotExportBar chartId="curva-chart-1"
              csvData={djdvData.map(p=>({ V:p.V, dJdV_mS_cm2:p.dJdV }))}
              csvFilename="curva_dJdV" plotFilename="curva_dJdV"
              c={c} disabled={djdvData.length===0} />
          </div>

          {/* Plot 3: dV/dJ — RED */}
          <div style={{padding:'16px 20px',borderRight: `1px solid ${c.border}`}}>
            <PT col={c.isDark ? '#f85149' : '#b91c1c'} c={c}>3 — dV/dJ vs 1/J → R<sub>s</sub>, A
              {liveRsA?.Rs!=null&&<Res col={c.isDark ? '#f85149' : '#b91c1c'}>Rs={fmt(liveRsA.Rs,3)}</Res>}
              {liveRsA?.A_dVdJ!=null&&<Res col={c.isDark ? '#f85149' : '#b91c1c'}>A={fmt(liveRsA.A_dVdJ,2)}</Res>}
            </PT>
            <Sub col={c.isDark ? '#f85149' : '#b91c1c'} c={c}>● Drag red lines to set fit window (1/J axis)</Sub>
            {dvdjData.length>0?(()=>{
              const invJMin = dvdjData.length ? Math.min(...dvdjData.map(p=>p.invJ)) : 0;
              const invJMax = dvdjData.length ? Math.max(...dvdjData.map(p=>p.invJ)) : 0.5;
              return (
              <div id="curva-chart-2">
              <InteractiveChart xMin={invJMin} xMax={invJMax} lines={[
                {color:C.rs, x:cursors.rs_invJ_min, label:'Rs min', onDrag:v=>setC('rs_invJ_min',Math.min(v,cursors.rs_invJ_max-0.01))},
                {color:C.rs, x:cursors.rs_invJ_max, label:'Rs max', onDrag:v=>setC('rs_invJ_max',Math.max(v,cursors.rs_invJ_min+0.01))},
              ]}>
                <ResponsiveContainer width="100%" height={CH}>
                  <LineChart data={dvdjData} margin={CM}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="invJ" tickFormatter={tickFmt(2)} tick={TK}
                      label={{value:'1/J (cm²/mA)',position:'insideBottom',offset:-18,...AX}}/>
                    <YAxis width={YW} tickFormatter={tickFmt(1)} tick={TK}
                      label={{value:'dV/dJ (Ω·cm²)',angle:-90,position:'center',dx:-10,...AX}}/>
                    <Tooltip formatter={v=>typeof v==='number'?v.toFixed(4):v}/>
                    <Line type="monotone" dataKey="dVdJ" stroke={C.rs} strokeWidth={2} dot={{r:3,fill:C.rs}} name="dV/dJ"/>
                  </LineChart>
                </ResponsiveContainer>
              </InteractiveChart>
              </div>
              );
            })():<ND c={c}/>}
            {liveRsA?.r2!=null&&<div style={{marginTop:6,fontSize:12,color:'#4a6278'}}>R² = {liveRsA.r2.toFixed(4)}</div>}
            <PlotExportBar chartId="curva-chart-2"
              csvData={dvdjData.map(p=>({ inv_J_cm2_per_mA:p.invJ, dVdJ_ohm_cm2:p.dVdJ, V:p.V }))}
              csvFilename="curva_dVdJ" plotFilename="curva_dVdJ"
              c={c} disabled={dvdjData.length===0} />
          </div>

          {/* Plot 4: log(J) — YELLOW */}
          <div style={{padding:'16px 20px'}}>
            <PT col={c.isDark ? '#d29922' : '#8a5a00'} c={c}>4 — log(J) vs V → J<sub>0</sub>, A
              {effJ0!=null&&<Res col={c.isDark ? '#d29922' : '#8a5a00'}>J₀={fmtExp(effJ0)}</Res>}
              {effA !=null&&<Res col={c.isDark ? '#d29922' : '#8a5a00'}>A={fmt(effA,2)}</Res>}
            </PT>
            <Sub col={c.isDark ? '#d29922' : '#8a5a00'} c={c}>● Drag yellow lines to set J₀/A fit range</Sub>
            {logjData.length>0?(
              <div id="curva-chart-3">
              <InteractiveChart xMin={vMin} xMax={vMax} lines={[
                {color:C.logj, x:cursors.logj_V_min, label:'J₀ min', onDrag:v=>setC('logj_V_min',Math.min(v,cursors.logj_V_max-0.05))},
                {color:C.logj, x:cursors.logj_V_max, label:'J₀ max', onDrag:v=>setC('logj_V_max',Math.max(v,cursors.logj_V_min+0.05))},
              ]}>
                <ResponsiveContainer width="100%" height={CH}>
                  <LineChart data={logjData} margin={CM}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="V" tickFormatter={v=>v.toFixed(2)} tick={TK}
                      label={{value:'Voltage (V)',position:'insideBottom',offset:-18,...AX}}/>
                    <YAxis width={YW} tickFormatter={tickFmt(1)} tick={TK}
                      label={{value:'log₁₀(J)',angle:-90,position:'center',dx:-10,...AX}}/>
                    <Tooltip formatter={v=>typeof v==='number'?v.toFixed(4):v}/>
                    <Line type="monotone" dataKey="logJ" stroke={C.logj} strokeWidth={2} dot={false} name="log(J)"/>
                  </LineChart>
                </ResponsiveContainer>
              </InteractiveChart>
              </div>
            ):<ND c={c}/>}
            {liveJ0A?.r2!=null&&<div style={{marginTop:6,fontSize:12,color:'#4a6278'}}>R² = {liveJ0A.r2.toFixed(4)}</div>}
            <PlotExportBar chartId="curva-chart-3"
              csvData={logjData.map(p=>({ V:p.V, log10_J:p.logJ }))}
              csvFilename="curva_logJ" plotFilename="curva_logJ"
              c={c} disabled={logjData.length===0} />
          </div>

        </div>

        {/* Comparison table */}
        <div style={{padding:'20px 24px'}}>
          <div style={{fontSize:15,letterSpacing:'2px',textTransform:'uppercase',color: c.text,marginBottom:14}}>
            Parameter Extraction — Auto CurVA vs Interactive Fit
          </div>
          <CmpTable auto={auto} liveRsh={liveRsh} liveRsA={liveRsA} effJ0={effJ0} effA={effA} liveMetrics={liveMetrics} saved={savedResult} c={c}/>
        </div>

      </div>
    </div>
  );
}

function AxisControls({ fields, c }) {
  // `c` is always passed from CurvaPanel now; the fallback keeps the
  // dark-theme look if a caller forgets.
  c = c || {
    input: '#131920', inputText: '#00d4ff', border: '#1e2d3d',
    accent: '#4a9eff', textMuted: '#6a8299',
  };
  const iBase = {
    background: c.input, color: c.inputText,
    fontFamily: 'IBM Plex Mono,monospace',
    fontSize: 13, padding: '2px 5px', outline: 'none', textAlign: 'right',
    border: 'none', width: 50,
  };
  const arrowStyle = {
    background: 'none', border: 'none', color: c.accent, cursor: 'pointer',
    padding: '0 2px', fontSize: 10, lineHeight: 1, fontFamily: 'monospace',
    userSelect: 'none',
  };

  // Each field gets its own local state via a sub-component
  function SpinField({ label, val, onChange, stepHint }) {
    const [local, setLocal] = React.useState(val == null ? '' : String(val));
    const focused = React.useRef(false);
    React.useEffect(() => {
      if (!focused.current) setLocal(val == null ? '' : String(val));
    }, [val]);
    const commit = (raw) => {
      const n = parseFloat(raw);
      if (!isNaN(n)) onChange(n);
      else if (raw === '' || raw === 'auto') onChange(null);
    };
    const step = stepHint || (val != null && Math.abs(val) < 2 ? 0.05 : 5);
    const nudge = (dir) => {
      const cur = parseFloat(local); const base = isNaN(cur) ? 0 : cur;
      const next = Math.round((base + dir * step) * 1e6) / 1e6;
      setLocal(String(next)); onChange(next);
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 13, color: c.textMuted, whiteSpace: 'nowrap' }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center',
          background: c.input,
          border: `1px solid ${c.border}`, borderRadius: 4, overflow: 'hidden' }}>
          <input type="text" style={iBase} value={local} placeholder="auto"
            onChange={e => setLocal(e.target.value)}
            onFocus={() => { focused.current = true; }}
            onBlur={() => { focused.current = false; commit(local); }}
            onKeyDown={e => {
              if (e.key === 'Enter') e.target.blur();
              if (e.key === 'ArrowUp')   { e.preventDefault(); nudge(+1); }
              if (e.key === 'ArrowDown') { e.preventDefault(); nudge(-1); }
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column',
                         borderLeft: `1px solid ${c.border}` }}>
            <button style={{ ...arrowStyle, borderBottom: `1px solid ${c.border}` }} onClick={() => nudge(+1)}>▲</button>
            <button style={arrowStyle} onClick={() => nudge(-1)}>▼</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px',
      padding: '8px 4px 2px', borderTop: `1px solid ${c.border}`, marginTop: 6 }}>
      {fields.map(f => (
        <SpinField key={f.label} label={f.label} val={f.val}
          onChange={f.onChange} stepHint={f.step} />
      ))}
    </div>
  );
}

function CmpTable({auto,liveRsh,liveRsA,effJ0,effA,liveMetrics,saved,c}) {
  const lm = liveMetrics;
  const rows=[
    ['Voc','V',4,auto?.Voc,lm?.Voc,saved?.Voc],
    ['Jsc','mA/cm²',3,auto?.Jsc!=null?Math.abs(auto.Jsc):null,lm?.Jsc!=null?Math.abs(lm.Jsc):null,saved?.Jsc!=null?Math.abs(saved.Jsc):null],
    ['FF','%',2,auto?.FF,lm?.FF,saved?.FF],
    ['PCE','%',3,auto?.PCE,lm?.PCE,saved?.PCE],
    ['Vmp','V',3,auto?.Vmp,lm?.Vmp,saved?.Vmp],
    ['Jmp','mA/cm²',3,auto?.Jmp!=null?Math.abs(auto.Jmp):null,lm?.Jmp!=null?Math.abs(lm.Jmp):null,saved?.Jmp!=null?Math.abs(saved.Jmp):null],
    ['J₀','mA/cm²',null,auto?.J0,effJ0,saved?.J0],
    ['A (logJV)','',4,auto?.A_logJV,effA,saved?.A_logJV],
    ['Rs','Ω·cm²',4,auto?.Rs,liveRsA?.Rs,saved?.Rs],
    ['Rsh','Ω·cm²',1,auto?.Rsh,liveRsh,saved?.Rsh],
    ['A (dV/dJ)','',4,auto?.A_dVdJ,liveRsA?.A_dVdJ,saved?.A_dVdJ],
  ];
  const fv=(v,d)=>v==null?'—':d==null?Number(v).toExponential(3):Number(v).toFixed(d);
  return(
    <div style={{display:'grid',gridTemplateColumns:'180px 1fr 1fr 1fr 90px',border: `1px solid ${c.border}`,borderRadius:4,overflow:'hidden'}}>
      {['Parameter','Auto CurVA','Interactive','Saved','Δ'].map((h,i)=>(
        <div key={h} style={{padding:'10px 14px',background: c.input,
          borderRight:i<4?'1px solid #1e2d3d':'none',borderBottom: `1px solid ${c.border}`,
          fontFamily:'IBM Plex Mono,monospace',fontSize:14,color: c.textMuted,letterSpacing:'1.5px',textTransform:'uppercase'}}>{h}</div>
      ))}
      {rows.map(([name,unit,dec,aV,lV,sV],idx)=>{
        const diff=aV!=null&&sV!=null?((sV-aV)/Math.abs(aV||1)*100):null;
        const bg=idx%2===0?c.bg:c.bgSecondary;
        const cs={padding:'8px 14px',background:bg,borderBottom: `1px solid ${c.border}`,fontFamily:'IBM Plex Mono,monospace',fontSize:16};
        return(
          <React.Fragment key={name}>
            <div style={{...cs,borderRight: `1px solid ${c.border}`,color: c.text,fontSize:15}}>{name} <span style={{color: c.textDim,fontSize:12}}>{unit}</span></div>
            <div style={{...cs,borderRight: `1px solid ${c.border}`,color: c.inputText}}>{fv(aV,dec)}</div>
            <div style={{...cs,borderRight: `1px solid ${c.border}`,color:lV!=null?c.accent:c.textMuted}}>{fv(lV,dec)}</div>
            <div style={{...cs,borderRight: `1px solid ${c.border}`,color:sV!=null?c.green:c.textDim}}>{fv(sV,dec)}</div>
            <div style={{...cs,fontSize:12,textAlign:'right',
              color:diff==null?c.textDim:Math.abs(diff)<2?c.green:Math.abs(diff)<10?c.amber:c.red}}>
              {diff!=null?`${diff>0?'+':''}${diff.toFixed(1)}%`:'—'}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Small helpers — style depends on the surrounding theme-aware colour
// map, so callers pass their `c` in explicitly.
const PT  = ({children,col,c}) => <div style={{fontFamily:'IBM Plex Mono,monospace',fontSize:13,fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase',color:col||(c?c.accentDark:'#a8bfd4'),marginBottom:4,borderBottom: c?`1px solid ${c.border}`:'1px solid #1e2d3d',paddingBottom:6}}>{children}</div>;
const Sub = ({children,col,c}) => <div style={{fontSize:14,color:col||(c?c.textMuted:'#4a6278'),marginBottom:8,opacity:0.85}}>{children}</div>;
const Res = ({children,col}) => <span style={{color:col,marginLeft:10,fontSize:13,textTransform:'none',letterSpacing:0}}>{children}</span>;
const ND  = ({c}) => <div style={{height:CH,display:'flex',alignItems:'center',justifyContent:'center',color:c?c.textMuted:'#2a3f55',fontFamily:'IBM Plex Mono,monospace',fontSize:14}}>No data — run a simulation or upload a file</div>;
